import { CalendarDays, Clock, MapPin, UserRound } from 'lucide-react'

import { issueStatusLabel } from '@/shared/constants/issue-status'

import { ageDays } from '../../../lib/issues'
import { DetailChip, SeverityChip, StatusChip } from '../detail/DetailChips'
import { Section } from '../detail/DetailSection'
import { GlanceGrid, type GlanceFact } from '../detail/GlanceGrid'
import { PeopleCard, type Person } from '../detail/PeopleCard'

import type { GisIssueDetail } from '../../../api/types'
import type { LucideIcon } from 'lucide-react'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** The HOD row (contact-only). */
function buildHod(detail: GisIssueDetail): Person {
  const dept = detail.departmentName
  return {
    roleLabel: dept ? `Head of Dept · ${dept}` : 'Head of Dept',
    name: detail.hodName ?? '',
    avatar: 'hod',
    phone: detail.hodPhone ?? null,
    email: detail.hodEmail ?? null,
    sub: detail.hodRole ?? null,
  }
}

/** One label + value meta line with a leading glyph; renders nothing when the value is absent. */
function MetaRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string | null }) {
  if (!value) {
    return null
  }
  return (
    <div className="flex items-start gap-[9px]">
      <span className="mt-[1px] grid h-[24px] w-[24px] flex-none place-items-center rounded-lg bg-gis-track text-gis-soft">
        <Icon className="h-[13px] w-[13px] stroke-[2.2]" />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-bold tracking-[.06em] text-gis-subheading uppercase">{label}</div>
        <div className="mt-[2px] text-[12.5px] font-semibold text-gis-ink">{value}</div>
      </div>
    </div>
  )
}

/**
 * Right column of the gallery modal — the full issue read-out: severity + status chips, title and
 * human ref, the reported meta (site / date / reporter), description, a Category + Status facts grid,
 * and the Responsible Person roster with inline contacts.
 */
export function IssueModalDetails({ detail }: { detail: GisIssueDetail }) {
  // v2: the HOD (with their department) is the responsible party — the FO only raises.
  const people: Person[] = [buildHod(detail)]
  const age = ageDays(detail.raisedAt)
  const facts: GlanceFact[] = [
    { label: 'Category', value: detail.sectionTitle ?? '—' },
    { label: 'Status', value: issueStatusLabel(detail.status) },
  ]

  return (
    <div className="@container/modaldetail flex min-w-0 flex-col">
      <div className="mb-3.5">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <SeverityChip severity={detail.severity} />
          <StatusChip status={detail.status} />
          <DetailChip tabular>
            <Clock className="mr-[5px] h-[12px] w-[12px]" />
            {age} day{age === 1 ? '' : 's'} old
          </DetailChip>
        </div>
        <h2 className="text-[17px] leading-[1.26] font-bold tracking-[-.02em] text-gis-ink">{detail.title}</h2>
      </div>

      <div className="mb-4 space-y-2.5">
        <MetaRow icon={MapPin} label="Site" value={detail.siteName} />
        <MetaRow icon={CalendarDays} label="Reported" value={formatDate(detail.raisedAt)} />
        <MetaRow icon={UserRound} label="Reported by" value={detail.raisedByName} />
      </div>

      {detail.description && (
        <Section label="Description">
          <div className="rounded-2xl border border-gis-line bg-white px-4 py-3">
            <p className="text-[13.5px] leading-[1.55] whitespace-pre-wrap text-gis-ink">{detail.description}</p>
          </div>
        </Section>
      )}

      <Section label="Details">
        <GlanceGrid facts={facts} />
      </Section>

      <Section label="Responsible Person">
        <PeopleCard people={people} />
      </Section>
    </div>
  )
}
